import { readonly, ref, useContext } from "@nuxtjs/composition-api";
import { Logger } from "~/helpers/logger";
import { placeOrderCommand } from "~/modules/checkout/composables/useMakeOrder/commands/placeOrderCommand";
import useCart from "~/modules/checkout/composables/useCart";
import type { Order, OrderAddress, OrderDataOutput, OrderItem, PlaceOrderOutput } from "~/modules/GraphQL/types";
import { ComposableFunctionArgs, useSearchProducts } from "~/composables";
import type { UseMakeOrderErrors, UseMakeOrderInterface } from "./useMakeOrder";
import oderDataGql from "./queries/oderData.gql";
import { useClevertapStore } from "~/stores/clevertap";
import { useCartView } from "~/modules/checkout/composables/useCartView";
import { useUser } from "~/modules/customer/composables/useUser";
import { GET_PRODUCT_SEARCH } from "~/modules/catalog/product/queries/getProduct.gql";
import { useEcommercePixel } from "~/stores/ecommercePixel"; // Add this import
import {
  getPrice,
} from "~/modules/checkout/helpers";
interface ProductsResponse {
  productSearch: {
    items: any[];
    [key: string]: any;
  };
}
/**
 * Allows making an order from a cart.
 *
 * See the {@link UseMakeOrderInterface} for a list of methods and values available in this composable.
 */
export function useMakeOrder(): UseMakeOrderInterface {
  const loading = ref(false);
  const error = ref<UseMakeOrderErrors>({ make: null, orderData: null });
  const { cart } = useCart();
  const context = useContext();
  const clevertapStore = useClevertapStore();
  const cartView = useCartView();
  const { isAuthenticated, user } = useUser();
  const { fetchProducts } = useSearchProducts();

  // Function to track detailed order creation event
  const trackOrderCreated = async (order: any) => {
    if (!process.client || !order || !order.order_number) return;

    try {
      const win = window as any;
      if (!win.clevertap) return;

      // Get order details from the order object
      const orderItems = order.order_items || [];
      const orderInfo = order.order_info || {};
      const shippingAddress = orderInfo.order_address || {};
      
      // Calculate order totals from order data instead of cart
      let orderQuantity = 0;
      orderItems.forEach(item => {
        orderQuantity += item.qty_ordered || 0;
      });
      
      // Track items that came from recommendations
      const fromToData = clevertapStore.getFromtTo;
      const isSuggestedProduct = fromToData?.suggest_product === true;
      
      // Base order data
      const trackingData: Record<string, any> = {
        order_id: order.order_number,
        order_revenue: orderInfo.grand_total || 0,
        order_quantity: orderQuantity,
        order_subtotal: orderInfo.subtotal || 0,
        order_shipping_amount: orderInfo.shipping_amount || 0,
        voucher_discount_amount: Math.abs(orderInfo.discount_amount || 0), // Initialize with 0, will update if vouchers exist
        promotion_discount_amount: Math.abs(orderInfo.discount_amount || 0),
        order_discount_amount: Math.abs(orderInfo.discount_amount || 0),
        order_currency: 'VND',
        payment_method: orderInfo.payment_method || '',
        is_logged_in: isAuthenticated.value,
        suggest_product: isSuggestedProduct,
        suggest_product_count: 0,
        is_repurchased: false,
      };

      // Add voucher information if available
      if (orderInfo.erp_voucher_codes) {
        const voucherCodes = Array.isArray(orderInfo.erp_voucher_codes) 
          ? orderInfo.erp_voucher_codes 
          : [orderInfo.erp_voucher_codes];
          
        voucherCodes.forEach((coupon, index) => {
          if (!coupon) return;
          
          const voucherIndex = index + 1;
          trackingData[`voucher_code_${voucherIndex}`] = coupon;
          trackingData[`voucher_name_${voucherIndex}`] = coupon;
        });
      }
      
      // Add shipping information from the actual order data
      if (shippingAddress) {
        const street = Array.isArray(shippingAddress.street) 
          ? shippingAddress.street.join(', ') 
          : shippingAddress.street || '';
        
        trackingData.shipping_address = street;
        trackingData.shipping_city = shippingAddress.region || '';
        trackingData.shipping_district = shippingAddress.city || '';
        trackingData.shipping_ward = shippingAddress.ward_name || '';
        trackingData.shipping_full_name = shippingAddress.firstname + ' ' + (shippingAddress.lastname || '');
        trackingData.shipping_phone_number = shippingAddress.telephone || '';
      }
      
      // Process each item in the order
      orderItems.forEach((item, index) => {
        const itemIndex = index + 1;
        const itemSku = item.sku || '';
        const parentSku = itemSku.split('-')[0];
        
        // Extract size and color from SKU if available
        const skuParts = itemSku.split('-');
        let color = '';
        let size = '';
        
        if (skuParts.length >= 3) {
          color = skuParts[1] || '';
          size = skuParts[2] || '';
        }
        
        // Calculate item revenue
        const itemPrice = item.price || 0;
        const itemQuantity = item.qty_ordered || 0;
        const itemRevenue = itemPrice * itemQuantity;
        
        // Add item-specific properties with index
        trackingData[`product_id_${itemIndex}`] = parentSku;
        trackingData[`item_id_${itemIndex}`] = itemSku;
        trackingData[`item_name_${itemIndex}`] = item.name || '';
        trackingData[`item_price_${itemIndex}`] = itemPrice;
        trackingData[`item_revenue_${itemIndex}`] = itemRevenue;
        trackingData[`item_subtotal_${itemIndex}`] = itemRevenue;
        trackingData[`item_quantity_${itemIndex}`] = itemQuantity;
        trackingData[`item_color_name_${itemIndex}`] = color;
        trackingData[`item_size_${itemIndex}`] = size;
        
        // Check if this is a suggested product
        if (fromToData && 
            fromToData.suggest_product === true && 
            parentSku === fromToData.from_product_id) {
          trackingData.suggest_product_count++;
          trackingData[`product_suggest_name_${trackingData.suggest_product_count}`] = item.name;
        }
      });
      
      // Add loyalty amount if available
      if (orderInfo.loyalty_amount) {
        trackingData.loyalty_discount_amount = orderInfo.loyalty_amount;
      }
      
      // Send event to CleverTap
      clevertapStore.orderCreatedCleverTap({
        clevertap: win.clevertap,
        data: trackingData,
      });
    } catch (error) {
      console.error('Error tracking order creation:', error);
      // Don't throw the error to avoid disrupting the order process
    }
  };

  // Function to track payment events
  const trackPaymentEvent = (orderId: string, isSuccess: boolean, errorMessage?: string) => {
    if (process.client) {
      try {
        const win = window as any;
        if (!win.clevertap) return;

        const checkoutId = context.app.$vsf.$magento.config.state.getCartId();
        const paymentMethod = cart.value?.selected_payment_method?.code || "";
        
        // Get payment method display name
        let paymentMethodName = "Unknown";
        if (paymentMethod === "cashondelivery") {
          paymentMethodName = "Thanh toán khi nhận hàng (COD)";
        } else if (paymentMethod === "vnpay") {
          paymentMethodName = "Thanh toán bằng VNPAY";
        } else if (paymentMethod === "shopee_pay") {
          paymentMethodName = "Thanh toán bằng ShopeePay";
        }

        // Common data for both success and failure events
        const trackingData = {
          checkout_id: checkoutId,
          order_id: orderId,
          payment_method: paymentMethodName,
          payment_amount: cartView.totals.value?.subtotal || 0,
          payment_revenue: cartView.totals.value?.subtotal || 0
        };

        // Add error message to failure event
        if (!isSuccess && errorMessage) {
          Object.assign(trackingData, { 
            error_message: errorMessage
          });
        }

        // Track the appropriate event
        if (isSuccess) {
          clevertapStore.paymentSuccessfulCleverTap({
            clevertap: win.clevertap,
            data: trackingData
          });
          
          // Only track purchase events if payment is successful
          if (cartView.products.value.length > 0) {
            const ecommercePixelStore = useEcommercePixel();
            const totalAmount = cartView.totals.value?.total || 0;
            // Prepare product data for tracking
            const products = cartView.products.value.map(item => {
              const product = item.product;
              const productParent = item.productParent;
              const parentSku = product.sku?.split('-').slice(0, -1).join('-');
              
              const skuParts = product.sku.split('-');
              const color = skuParts.length > 1 ? skuParts[1] : '';
              const size = skuParts.length > 2 ? skuParts[2] : '';
              const priceInfo = getPrice(item);
              
              return {
                item_id: product.sku,
                item_name: product.name,
                final_price: priceInfo.final || 0,
                price: priceInfo.special || priceInfo.regular || 0,
                quantity: item.quantity,
                item_brand: "CNF",
                item_variant: `${color} ${size}`.trim(),
                item_category: productParent?.categories?.[0]?.name || "",
                parent_sku: parentSku
              };
            });
            
            // Facebook Pixel Purchase Event
            if (win.fbq) {
              ecommercePixelStore.purchaseFbq({
                analytics: win.fbq,
                order: {
                  id: orderId,
                  currency: "VND",
                  value: totalAmount,
                  shipping: cartView.totals.value?.shipping_amount || 0,
                  tax: cartView.totals.value?.tax_amount || 0,
                  coupon: cart.value?.applied_coupons?.[0]?.code || "",
                  contents: products.map(p => ({
                    id: p.parent_sku,
                    quantity: p.quantity,
                    item_price: p.final_price
                  }))
                },
                userData: isAuthenticated.value && user.value ? user.value : null
              });
            }
            
            // Google Tag Manager Purchase Event
            if (win.dataLayer) {
              // Standard GTM tracking
              ecommercePixelStore.purchaseGtag({
                dataLayer: win.dataLayer,
                window_dataLayer: win.dataLayer,
                order: {
                  transaction_id: orderId,
                  value: totalAmount,
                  tax: cartView.totals.value?.tax_amount || 0,
                  shipping: cartView.totals.value?.shipping_amount || 0,
                  currency: "VND",
                  coupon: cart.value?.applied_coupons?.[0]?.code || "",
                  items: products
                }
              });
              
              // Google Analytics 4 Retail tracking
              ecommercePixelStore.purchaseGa4({
                dataLayer: win.dataLayer,
                window_dataLayer: win.dataLayer,
                order: {
                  transaction_id: orderId,
                  value: totalAmount,
                  tax: cartView.totals.value?.tax_amount || 0,
                  shipping: cartView.totals.value?.shipping_amount || 0,
                  currency: "VND",
                  coupon: cart.value?.applied_coupons?.[0]?.code || "",
                  items: products
                }
              });
            }
            
            // TikTok Pixel Purchase Event
            if (win.ttq) {
              ecommercePixelStore.purchaseTiktok({
                ttq: win.ttq,
                order: {
                  contents: products.map(p => ({
                    content_id: p.parent_sku,
                    content_type: 'product',
                    content_name: p.item_name,
                    quantity: p.quantity,
                    price: p.price
                  })),
                  value: totalAmount,
                  currency: "VND",
                  order_id: orderId
                }
              });
            }
          }
        } else {
          clevertapStore.paymentFailedCleverTap({
            clevertap: win.clevertap,
            data: trackingData
          });
        }
      } catch (error) {
        console.error(`Error tracking payment ${isSuccess ? 'success' : 'failure'}:`, error);
      }
    }
  };

  const make = async (params?: ComposableFunctionArgs<{}>): Promise<PlaceOrderOutput | null> => {
    Logger.debug("useMakeOrder.make");
    let placedOrder = null;
    try {
      loading.value = true;
      placedOrder = await placeOrderCommand.execute(context, cart.value.id, params);
      error.value.make = null;
      
      // Track successful payment if order is placed successfully
      if (placedOrder?.order?.order_number) {
        trackPaymentEvent(placedOrder.order.order_number, true);
        
        // Also track detailed order created event
        await trackOrderCreated(placedOrder.order);
      }
      
      return placedOrder;
    } catch (err) {
      error.value.make = err;
      Logger.error("useMakeOrder.make", err);
      
      // Track failed payment with error message
      trackPaymentEvent(
        cart.value?.id || 'unknown', 
        false, 
        err.message || placedOrder?.errors?.[0]?.message 
      );
    } finally {
      loading.value = false;
    }
  };

  const getOrderData = async (orderId: string, phoneNumber=""): Promise<Order | null> => {
    Logger.debug("useMakeOrder.orderData", orderId);
    try {
      loading.value = true;
      context.app.$cookies?.remove("phone_number");
      if (!isAuthenticated.value && phoneNumber) {
        context.app.$cookies?.set("phone_number", phoneNumber);
      }
      const { data } = await context.app.$vsf.$magento.api.customQuery({
        query: oderDataGql,
        queryVariables: { orderId: orderId },
      });
      error.value.orderData = null;

      const typedData = data as OrderDataOutput;
      const orderData = typedData?.orderData;
      const orderItems = orderData?.order_items as OrderItem[];

      const productSkus = orderItems?.map((item) => item.sku);
      const productsParentSkus = [
        ...new Set([
          ...productSkus,
          ...productSkus.map((sku) => sku.split("-")[0]),
          ...productSkus.map((sku) => {
            const parts = sku.split("-");
            return parts.length > 1 ? `${parts[0]}-${parts[1]}` : parts[0];
          }),
        ]),
      ];
      if (productsParentSkus.length > 0) {
         const params = {
                skus: productsParentSkus,
              };
              const { data } = await fetchProducts(params);
              const productsParent = data || null;
        
        // Add parent products to order items
        if (productsParent &&  productsParent.length > 0) {
          typedData.orderData.order_items = orderItems.map((item) => {
            const productParent = productsParent.find((parent) => {
              const parentChild = parent.configurable_children.find((child) => {
                if (child.sku === item.sku) {
                  return true;
                }
                return false;
              });
              if (parentChild) {
                return true;
              }
              return false;
            });
            
            if (productParent) {
              return {
                ...item,
                productParent
              };
            }
            return item;
          });
        }
      }
      return typedData?.orderData;
    } catch (err) {
      error.value.orderData = err;
      Logger.error("useMakeOrder.orderData", err);
      return null;
    } finally {
      loading.value = false;
    }
  };

  const getProductListBySkus = async (skus: string[]) => {
    const { data, errors } = await context.app.$vsf.$magento.api.customQuery({
      query: GET_PRODUCT_SEARCH,
      queryVariables: { filter: { sku: { in: skus } } },
    });

    const typedData = data as unknown as ProductsResponse;
    return typedData?.productSearch ?? { items: [] };
  };

  return {
    make,
    getOrderData,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from "./useMakeOrder";
export default useMakeOrder;
